import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ShoppingBag, Star, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

// Handle image URL - support both uploaded files and external URLs
const getImageUrl = (img) => {
  if (!img) return 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=400';
  if (img.startsWith('http://') || img.startsWith('https://')) {
    return img;
  }
  if (img.startsWith('/api/uploads/') || img.startsWith('/uploads/')) {
    const path = img.startsWith('/uploads/') ? img.replace('/uploads/', '/api/uploads/') : img;
    return `${process.env.REACT_APP_BACKEND_URL}${path}`;
  }
  return img;
};

export const ProductCard = ({ product, index = 0 }) => {
  const { t } = useTranslation();
  const { isAuthenticated, openAuthModal } = useAuth();
  const { addToCart } = useCart();

  const price = product.price_retail;
  const imageUrl = getImageUrl(product.images?.[0]);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      openAuthModal('login');
      toast.info('Connectez-vous pour ajouter au panier');
      return;
    }

    try {
      await addToCart(product.product_id, 1);
      toast.success(`${product.name} ajouté au panier`);
    } catch (error) {
      toast.error("Erreur lors de l'ajout au panier");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      data-testid={`product-card-${product.product_id}`}
    >
      <Link 
        to={`/products/${product.product_id}`}
        className="group block bg-brand-white overflow-hidden transition-all duration-500 card-lift border border-brand-gold/10"
      >
        {/* Image Container */}
        <div className="relative aspect-[3/4] overflow-hidden bg-brand-cream">
          <img 
            src={imageUrl}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=400'; }}
          />
          
          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {product.is_new && (
              <span className="bg-brand-black text-brand-white text-xs font-medium px-3 py-1.5 uppercase tracking-wider flex items-center gap-1 font-body">
                <Sparkles className="h-3 w-3" /> {t('products.new')}
              </span>
            )}
            {product.is_bestseller && (
              <span className="bg-brand-gold text-brand-black text-xs font-medium px-3 py-1.5 uppercase tracking-wider flex items-center gap-1 font-body">
                <Star className="h-3 w-3" /> {t('products.bestseller')}
              </span>
            )}
          </div>

          {/* Quick Add Button */}
          <div className="absolute inset-x-0 bottom-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
            <Button 
              onClick={handleAddToCart}
              className="w-full bg-brand-black text-brand-white hover:bg-brand-gold hover:text-brand-black font-medium py-3 uppercase tracking-wider rounded-none transition-all duration-300 text-sm"
              data-testid={`add-to-cart-${product.product_id}`}
            >
              <ShoppingBag className="h-4 w-4 mr-2" /> {t('products.addToCart')}
            </Button>
          </div>

          {/* Out of Stock Overlay */}
          {product.stock <= 0 && (
            <div className="absolute inset-0 bg-brand-black/70 flex items-center justify-center">
              <span className="bg-brand-white text-brand-black px-4 py-2 font-medium text-sm uppercase tracking-wider font-body">{t('products.outOfStock')}</span>
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="p-4 sm:p-5 space-y-2 bg-brand-white">
          <h3 className="font-heading text-lg text-brand-dark group-hover:text-brand-gold transition-colors line-clamp-1">
            {product.name}
          </h3>
          <div className="flex items-center gap-3">
            <span className="text-brand-gold font-heading text-xl font-bold">
              ${price.toFixed(2)} <span className="text-brand-dark/50 text-sm font-body font-normal">CAD</span>
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
